/**
 * Interrupt Approval Hook
 *
 * Manages approval UI (modal or banner) based on section count.
 * - When >1 sections affected: Shows ApprovalModal with side-by-side comparison
 * - When 1 section affected: Shows ApprovalBanner inline sticky banner
 * - When 0 sections affected: Shows neither
 *
 * Encapsulates all modal lifecycle logic and banner rendering.
 * Reads state from Redux and only requires sections and callback as params.
 */

import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import type { NoteSection } from '@features/notes';
import { useAppSelector } from '@store/hooks';
import { useModal } from '@store/hooks/useModal';
import { selectIsModalTypeOpen } from '@store/selectors/modalSelectors';

import { ApprovalBanner } from '../components/ApprovalBanner';
import { LAYOUT_SELECTORS } from '../constants/layout';
import {
  selectAffectedSectionIds,
  selectAssistantStatus,
  selectInterrupt,
} from '../store/selectors';
import { approvalHandlersRegistry } from '../utils/approvalHandlerRegistry';

import { useApprovalNavigation } from './useApprovalNavigation';

export interface UseInterruptApprovalParams {
  /** Note sections (from RTK Query) */
  sections: NoteSection[];
  /** Approval handler callback */
  handleApproval: (confirmed: boolean) => void;
}

export interface UseInterruptApprovalReturn {
  /** Banner JSX to render (undefined when using modal) */
  banner: React.ReactNode;
}

/**
 * Hook for managing interrupt approval UI
 *
 * Features:
 * - Section count controls modal vs banner UX (>1 sections = modal, 1 section = banner)
 * - Reads state from Redux (interrupt, assistantStatus, affectedSectionIds)
 * - Handles modal lifecycle (open/close)
 * - Renders banner when exactly 1 section is affected
 *
 * @param sections - Note sections from RTK Query
 * @param handleApproval - Callback for approval/rejection
 * @returns Banner element (or undefined if using modal or no sections affected)
 */
export const useInterruptApproval = ({
  sections,
  handleApproval,
}: UseInterruptApprovalParams): UseInterruptApprovalReturn => {
  const { noteId, encounterId } = useParams<{ noteId: string; encounterId: string }>();
  const { open: openModal, close: closeModal } = useModal();

  // Redux state
  const interrupt = useAppSelector(selectInterrupt);
  const assistantStatus = useAppSelector(selectAssistantStatus);
  const affectedSectionIds = useAppSelector(selectAffectedSectionIds);
  const isApprovalModalOpen = useAppSelector(state =>
    selectIsModalTypeOpen(MODAL_TYPES.APPROVAL_MODAL)(state),
  );

  // Check if this is a note_changes interrupt (should be skipped entirely)
  const isNoteChangesInterrupt = interrupt?.confirmationType === 'note_changes';

  // Determine UI mode based on interrupt edits count (for non-note_changes CONFIRMATION)
  // For note_changes, we never show modal (backend auto-accepts)
  const shouldUseModal =
    !isNoteChangesInterrupt &&
    interrupt?.eventType === 'CONFIRMATION' &&
    (interrupt?.edits?.length ?? 0) > 1;

  // Banner disabled - auto-scroll still works via useApprovalNavigation hook below
  const shouldUseBanner = false;

  // Approval navigation hook - provides auto-scroll to first affected section
  // Called regardless of banner visibility to ensure auto-scroll works
  const approvalNavigation = useApprovalNavigation(
    affectedSectionIds,
    LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER,
  );

  // MODAL PATH: Open ApprovalModal when interrupt is active and >1 sections affected
  // Skip note_changes confirmations (backend auto-accepts those)
  useEffect(() => {
    if (!shouldUseModal) return;

    const shouldShowApproval =
      interrupt?.isActive &&
      interrupt?.eventType === 'CONFIRMATION' &&
      interrupt?.confirmationType !== 'note_changes' && // Skip note_changes (backend auto-accepts)
      (!interrupt?.namespace || interrupt.namespace === 'global');

    if (shouldShowApproval && noteId && !isApprovalModalOpen) {
      // Store handlers in registry (avoids Redux serialization)
      approvalHandlersRegistry.set(noteId, {
        onApprove: () => handleApproval(true),
        onReject: () => handleApproval(false),
      });

      openModal(MODAL_TYPES.APPROVAL_MODAL, {
        maxWidth: 'xl',
        fullWidth: true,
        noteId,
        encounterId,
        sections,
        edits: interrupt?.edits || [],
        affectedSectionIds,
        message: interrupt?.message,
        confirmLabel: interrupt?.confirmLabel,
        cancelLabel: interrupt?.cancelLabel,
      });
    }

    // Cleanup: Remove handlers when interrupt becomes inactive
    return () => {
      const isInactive =
        !interrupt?.isActive ||
        interrupt?.eventType !== 'CONFIRMATION' ||
        interrupt?.confirmationType === 'note_changes' || // Skip note_changes
        (interrupt?.namespace && interrupt.namespace !== 'global');
      if (noteId && isInactive) {
        approvalHandlersRegistry.delete(noteId);
      }
    };
  }, [
    shouldUseModal,
    interrupt?.isActive,
    interrupt?.eventType,
    interrupt?.confirmationType,
    interrupt?.namespace,
    interrupt?.message,
    interrupt?.confirmLabel,
    interrupt?.cancelLabel,
    interrupt?.edits,
    affectedSectionIds,
    sections,
    noteId,
    encounterId,
    handleApproval,
    openModal,
    isApprovalModalOpen,
  ]);

  // MODAL PATH: Close when interrupt becomes inactive, is note_changes, OR when shouldUseModal becomes false
  useEffect(() => {
    const shouldCloseFromNoteChanges = isNoteChangesInterrupt && isApprovalModalOpen;
    const shouldCloseFromInterrupt =
      (!interrupt?.isActive || interrupt?.namespace !== 'global') && isApprovalModalOpen;
    const shouldCloseFromSectionCount = !shouldUseModal && isApprovalModalOpen;
    const shouldClose =
      shouldCloseFromNoteChanges || shouldCloseFromInterrupt || shouldCloseFromSectionCount;

    if (shouldClose) {
      closeModal();
    }
  }, [
    shouldUseModal,
    interrupt?.isActive,
    interrupt?.namespace,
    isNoteChangesInterrupt,
    isApprovalModalOpen,
    closeModal,
  ]);

  // BANNER PATH: Return banner JSX when exactly 1 section is affected
  // Skip note_changes confirmations (backend auto-accepts those)
  const banner = shouldUseBanner ? (
    <ApprovalBanner
      isVisible={Boolean(
        interrupt?.isActive &&
          interrupt?.eventType === 'CONFIRMATION' &&
          interrupt?.confirmationType !== 'note_changes' && // Skip note_changes
          (!interrupt?.namespace || interrupt.namespace === 'global'),
      )}
      message={interrupt?.eventType === 'CONFIRMATION' ? interrupt?.message : undefined}
      affectedCount={affectedSectionIds.length}
      onApprove={() => handleApproval(true)}
      onReject={() => handleApproval(false)}
      isProcessing={assistantStatus === 'thinking'}
      confirmLabel={interrupt?.confirmLabel}
      cancelLabel={interrupt?.cancelLabel}
      navigation={approvalNavigation}
    />
  ) : undefined;

  return { banner };
};
